import { SoundManager, SoundType } from '../audio/SoundManager';

/**
 * Help menu tabs
 */
enum HelpTab {
  FOOD = 'food',
  POWERUPS = 'powerups',
  CONTROLS = 'controls',
  AI_LORE = 'ai_lore',
  TIPS = 'tips',
  MAP = 'map',
}

/**
 * Tab configuration
 */
interface TabConfig {
  id: HelpTab;
  label: string;
  icon: string;
}

/**
 * All available tabs
 */
const TABS: TabConfig[] = [
  { id: HelpTab.FOOD, label: 'Food', icon: '🍎' },
  { id: HelpTab.POWERUPS, label: 'Powerups', icon: '⚡' },
  { id: HelpTab.CONTROLS, label: 'Controls', icon: '🎮' },
  { id: HelpTab.AI_LORE, label: 'AI Lore', icon: '🐍' },
  { id: HelpTab.TIPS, label: 'Tips', icon: '💡' },
  { id: HelpTab.MAP, label: 'Map', icon: '🗺️' },
];

/**
 * HelpMenu - Comprehensive help/info system with tabbed navigation
 *
 * Features:
 * - Food guide with all food types and values
 * - Powerups explanation
 * - Controls reference
 * - AI snake lore
 * - Tips and tricks
 * - Zone map
 */
export class HelpMenu {
  /** Container element (initialized in createUI) */
  private container!: HTMLDivElement;

  /** Content wrapper (initialized in createUI) */
  private content!: HTMLDivElement;

  /** Tab buttons */
  private tabButtons: Map<HelpTab, HTMLButtonElement> = new Map();

  /** Tab panels */
  private tabPanels: Map<HelpTab, HTMLDivElement> = new Map();

  /** Currently active tab */
  private activeTab: HelpTab = HelpTab.FOOD;

  /** Whether UI is visible */
  private visible: boolean = false;

  /** Keyboard listener reference for cleanup */
  private keydownListener: ((e: KeyboardEvent) => void) | null = null;

  /** Callback when menu is closed */
  private onClose: (() => void) | null = null;

  constructor() {
    this.injectStyles();
    this.createUI();
  }

  /**
   * Inject CSS animations and styles
   */
  private injectStyles(): void {
    if (document.getElementById('help-menu-styles')) return;

    const style = document.createElement('style');
    style.id = 'help-menu-styles';
    style.textContent = `
      @keyframes help-menu-fade-in {
        from { opacity: 0; transform: scale(0.95) translateY(-10px); }
        to { opacity: 1; transform: scale(1) translateY(0); }
      }
      @keyframes help-menu-fade-out {
        from { opacity: 1; transform: scale(1) translateY(0); }
        to { opacity: 0; transform: scale(0.95) translateY(-10px); }
      }
      .help-menu-section-title {
        color: #4ecdc4;
        font-size: 16px;
        font-weight: 700;
        margin: 16px 0 10px 0;
        text-transform: uppercase;
        letter-spacing: 1px;
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .help-menu-section-title:first-child {
        margin-top: 0;
      }
      .help-menu-table {
        width: 100%;
        border-collapse: collapse;
        margin: 8px 0;
        font-size: 13px;
      }
      .help-menu-table th {
        background: rgba(78, 205, 196, 0.15);
        color: #4ecdc4;
        padding: 8px 12px;
        text-align: left;
        font-weight: 600;
        border-bottom: 1px solid rgba(78, 205, 196, 0.3);
      }
      .help-menu-table td {
        padding: 6px 12px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        color: #ccc;
      }
      .help-menu-table tr:hover td {
        background: rgba(78, 205, 196, 0.05);
      }
      .help-menu-food-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
        gap: 8px;
        margin: 8px 0;
      }
      .help-menu-food-item {
        background: rgba(30, 30, 50, 0.6);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        padding: 8px 12px;
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        color: #ccc;
      }
      .help-menu-food-value {
        color: #4ecdc4;
        font-weight: 600;
        margin-left: auto;
      }
      .help-menu-lore-card {
        background: rgba(30, 30, 50, 0.6);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        padding: 16px;
        margin: 12px 0;
      }
      .help-menu-lore-header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 8px;
      }
      .help-menu-lore-tier {
        font-size: 14px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;
      }
      .help-menu-lore-names {
        font-size: 12px;
        color: #888;
        font-style: italic;
        margin-bottom: 8px;
      }
      .help-menu-lore-desc {
        font-size: 13px;
        color: #aaa;
        line-height: 1.5;
      }
      .help-menu-tip {
        background: rgba(78, 205, 196, 0.08);
        border-left: 3px solid #4ecdc4;
        padding: 10px 14px;
        margin: 8px 0;
        font-size: 13px;
        color: #ccc;
        line-height: 1.5;
      }
      .help-menu-zone-map {
        font-family: 'Courier New', monospace;
        font-size: 11px;
        line-height: 1.4;
        color: #aaa;
        background: rgba(20, 20, 35, 0.8);
        padding: 16px;
        border-radius: 8px;
        white-space: pre;
        overflow-x: auto;
      }
      .help-menu-key {
        display: inline-block;
        background: rgba(78, 205, 196, 0.2);
        border: 1px solid rgba(78, 205, 196, 0.4);
        border-radius: 4px;
        padding: 2px 8px;
        font-family: 'Courier New', monospace;
        font-size: 12px;
        color: #4ecdc4;
        margin: 0 2px;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Create the UI elements
   */
  private createUI(): void {
    // Create overlay container
    this.container = document.createElement('div');
    this.container.id = 'help-menu';
    this.container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: none;
      justify-content: center;
      align-items: center;
      background: rgba(10, 10, 20, 0.9);
      backdrop-filter: blur(4px);
      z-index: 950;
      font-family: 'Segoe UI', Arial, sans-serif;
      opacity: 0;
      transition: opacity 0.2s ease-out;
    `;

    // Content wrapper
    this.content = document.createElement('div');
    this.content.style.cssText = `
      max-width: 700px;
      width: 90%;
      max-height: 85vh;
      background: linear-gradient(145deg, rgba(40, 40, 70, 0.98), rgba(25, 25, 45, 0.99));
      border-radius: 20px;
      border: 2px solid rgba(78, 205, 196, 0.3);
      box-shadow: 0 15px 50px rgba(0, 0, 0, 0.5), 0 0 40px rgba(78, 205, 196, 0.1);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      animation: help-menu-fade-in 0.25s ease-out forwards;
    `;

    // Header
    const header = document.createElement('div');
    header.style.cssText = `
      padding: 20px 24px;
      border-bottom: 1px solid rgba(78, 205, 196, 0.2);
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-shrink: 0;
    `;

    const titleRow = document.createElement('div');
    titleRow.style.cssText = `display: flex; align-items: center; gap: 12px;`;

    const titleIcon = document.createElement('span');
    titleIcon.textContent = '📖';
    titleIcon.style.fontSize = '28px';
    titleRow.appendChild(titleIcon);

    const title = document.createElement('h2');
    title.textContent = 'HOW TO PLAY';
    title.style.cssText = `
      font-size: 24px;
      font-weight: 800;
      letter-spacing: 2px;
      margin: 0;
      color: #4ecdc4;
      text-shadow: 0 2px 10px rgba(78, 205, 196, 0.5);
    `;
    titleRow.appendChild(title);
    header.appendChild(titleRow);

    // Close button
    const closeButton = document.createElement('button');
    closeButton.innerHTML = '&times;';
    closeButton.style.cssText = `
      width: 36px;
      height: 36px;
      border: none;
      border-radius: 50%;
      background: rgba(255, 107, 107, 0.2);
      color: #ff9999;
      font-size: 24px;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    `;
    closeButton.addEventListener('click', () => {
      SoundManager.getInstance().play(SoundType.UI_CLICK);
      this.hide();
    });
    closeButton.addEventListener('mouseover', () => {
      SoundManager.getInstance().play(SoundType.UI_HOVER);
      closeButton.style.background = 'rgba(255, 107, 107, 0.4)';
      closeButton.style.color = '#ffbbbb';
    });
    closeButton.addEventListener('mouseout', () => {
      closeButton.style.background = 'rgba(255, 107, 107, 0.2)';
      closeButton.style.color = '#ff9999';
    });
    header.appendChild(closeButton);
    this.content.appendChild(header);

    // Tab bar
    const tabBar = document.createElement('div');
    tabBar.style.cssText = `
      display: flex;
      gap: 4px;
      padding: 12px 16px;
      background: rgba(20, 20, 35, 0.5);
      border-bottom: 1px solid rgba(78, 205, 196, 0.1);
      overflow-x: auto;
      flex-shrink: 0;
    `;

    for (const tab of TABS) {
      const tabBtn = document.createElement('button');
      tabBtn.innerHTML = `<span style="margin-right: 6px;">${tab.icon}</span>${tab.label}`;
      tabBtn.style.cssText = `
        padding: 10px 16px;
        font-size: 13px;
        font-weight: 600;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;
        ${tab.id === this.activeTab
          ? 'background: linear-gradient(145deg, #4ecdc4, #3db3ab); color: #fff; box-shadow: 0 3px 12px rgba(78, 205, 196, 0.3);'
          : 'background: rgba(40, 40, 60, 0.6); color: #888;'}
      `;
      tabBtn.addEventListener('click', () => {
        SoundManager.getInstance().play(SoundType.UI_CLICK);
        this.switchTab(tab.id);
      });
      tabBtn.addEventListener('mouseover', () => {
        SoundManager.getInstance().play(SoundType.UI_HOVER);
        if (tab.id !== this.activeTab) {
          tabBtn.style.color = '#bbb';
          tabBtn.style.background = 'rgba(50, 50, 70, 0.8)';
        }
      });
      tabBtn.addEventListener('mouseout', () => {
        if (tab.id !== this.activeTab) {
          tabBtn.style.color = '#888';
          tabBtn.style.background = 'rgba(40, 40, 60, 0.6)';
        }
      });
      this.tabButtons.set(tab.id, tabBtn);
      tabBar.appendChild(tabBtn);
    }
    this.content.appendChild(tabBar);

    // Tab panels container
    const panelsContainer = document.createElement('div');
    panelsContainer.style.cssText = `
      flex: 1;
      overflow-y: auto;
      padding: 20px 24px;
    `;

    // Create all tab panels
    this.tabPanels.set(HelpTab.FOOD, this.createFoodPanel());
    this.tabPanels.set(HelpTab.POWERUPS, this.createPowerupsPanel());
    this.tabPanels.set(HelpTab.CONTROLS, this.createControlsPanel());
    this.tabPanels.set(HelpTab.AI_LORE, this.createAILorePanel());
    this.tabPanels.set(HelpTab.TIPS, this.createTipsPanel());
    this.tabPanels.set(HelpTab.MAP, this.createMapPanel());

    for (const [id, panel] of this.tabPanels) {
      panel.style.display = id === this.activeTab ? 'block' : 'none';
      panelsContainer.appendChild(panel);
    }
    this.content.appendChild(panelsContainer);

    // Footer hint
    const footer = document.createElement('div');
    footer.style.cssText = `
      padding: 12px 24px;
      border-top: 1px solid rgba(78, 205, 196, 0.1);
      text-align: center;
      color: #666;
      font-size: 12px;
      flex-shrink: 0;
    `;
    footer.innerHTML = 'Press <span class="help-menu-key">ESC</span> to close';
    this.content.appendChild(footer);

    this.container.appendChild(this.content);
    document.body.appendChild(this.container);
  }

  /**
   * Create the Food Guide panel
   */
  private createFoodPanel(): HTMLDivElement {
    const panel = document.createElement('div');

    // Junk Food Section
    panel.innerHTML += `
      <div class="help-menu-section-title">
        <span>🍭</span> JUNK FOOD <span style="color: #888; font-size: 12px; font-weight: 400;">(0.5-3 segments)</span>
      </div>
    `;
    const junkGrid = document.createElement('div');
    junkGrid.className = 'help-menu-food-grid';
    junkGrid.innerHTML = `
      <div class="help-menu-food-item"><span>🍬</span> Candy <span class="help-menu-food-value">+0.5</span></div>
      <div class="help-menu-food-item"><span>🍭</span> Lollipop <span class="help-menu-food-value">+0.5</span></div>
      <div class="help-menu-food-item"><span>🍪</span> Cookie <span class="help-menu-food-value">+1</span></div>
      <div class="help-menu-food-item"><span>🥤</span> Soda <span class="help-menu-food-value">+1</span></div>
      <div class="help-menu-food-item"><span>🍩</span> Donut <span class="help-menu-food-value">+1.5</span></div>
      <div class="help-menu-food-item"><span>🍫</span> Chocolate <span class="help-menu-food-value">+2</span></div>
      <div class="help-menu-food-item"><span>🍟</span> Fries <span class="help-menu-food-value">+2</span></div>
      <div class="help-menu-food-item"><span>🍕</span> Pizza <span class="help-menu-food-value">+3</span></div>
      <div class="help-menu-food-item"><span>🌭</span> Hot Dog <span class="help-menu-food-value">+3</span></div>
    `;
    panel.appendChild(junkGrid);

    // Regular Food Section
    panel.innerHTML += `
      <div class="help-menu-section-title">
        <span>🥪</span> REGULAR FOOD <span style="color: #888; font-size: 12px; font-weight: 400;">(4-6 segments)</span>
      </div>
    `;
    const regularGrid = document.createElement('div');
    regularGrid.className = 'help-menu-food-grid';
    regularGrid.innerHTML = `
      <div class="help-menu-food-item"><span>🥚</span> Egg <span class="help-menu-food-value">+4</span></div>
      <div class="help-menu-food-item"><span>🍞</span> Bread <span class="help-menu-food-value">+4</span></div>
      <div class="help-menu-food-item"><span>🧀</span> Cheese <span class="help-menu-food-value">+5</span></div>
      <div class="help-menu-food-item"><span>🍗</span> Chicken <span class="help-menu-food-value">+5</span></div>
      <div class="help-menu-food-item"><span>🍚</span> Rice <span class="help-menu-food-value">+6</span></div>
    `;
    panel.appendChild(regularGrid);

    // Healthy Food Section
    panel.innerHTML += `
      <div class="help-menu-section-title">
        <span>🥗</span> HEALTHY FOOD <span style="color: #ffd700; font-size: 12px; font-weight: 400;">(7-10 segments - Worth hunting!)</span>
      </div>
    `;
    const healthyGrid = document.createElement('div');
    healthyGrid.className = 'help-menu-food-grid';
    healthyGrid.innerHTML = `
      <div class="help-menu-food-item"><span>🍎</span> Apple <span class="help-menu-food-value">+7</span></div>
      <div class="help-menu-food-item"><span>🥕</span> Carrot <span class="help-menu-food-value">+7</span></div>
      <div class="help-menu-food-item"><span>🍇</span> Grapes <span class="help-menu-food-value">+8</span></div>
      <div class="help-menu-food-item"><span>🥦</span> Broccoli <span class="help-menu-food-value">+8</span></div>
      <div class="help-menu-food-item"><span>🍓</span> Strawberry <span class="help-menu-food-value">+9</span></div>
      <div class="help-menu-food-item"><span>🥑</span> Avocado <span class="help-menu-food-value">+10</span></div>
    `;
    panel.appendChild(healthyGrid);

    // Essence Section
    panel.innerHTML += `
      <div class="help-menu-section-title">
        <span>💧</span> ESSENCE <span style="color: #888; font-size: 12px; font-weight: 400;">(Death Drops)</span>
      </div>
    `;
    const essenceGrid = document.createElement('div');
    essenceGrid.className = 'help-menu-food-grid';
    essenceGrid.innerHTML = `
      <div class="help-menu-food-item"><span>💧</span> Small <span class="help-menu-food-value">+1</span></div>
      <div class="help-menu-food-item"><span>💧</span> Medium <span class="help-menu-food-value">+3</span></div>
      <div class="help-menu-food-item"><span>💧</span> Large <span class="help-menu-food-value">+5</span></div>
    `;
    panel.appendChild(essenceGrid);

    // Feasts Section
    panel.innerHTML += `
      <div class="help-menu-section-title">
        <span>🎂</span> FEASTS <span style="color: #888; font-size: 12px; font-weight: 400;">(Merged Food)</span>
      </div>
    `;
    const feastTable = document.createElement('table');
    feastTable.className = 'help-menu-table';
    feastTable.innerHTML = `
      <tr>
        <th>Tier</th>
        <th>Threshold</th>
        <th>Visual</th>
      </tr>
      <tr><td style="color: #ffa726;">MEAL</td><td>+15</td><td>Warm glow</td></tr>
      <tr><td style="color: #ff7043;">DINNER</td><td>+30</td><td>Steam particles</td></tr>
      <tr><td style="color: #ec407a;">FEAST</td><td>+50</td><td>Sparkles</td></tr>
      <tr><td style="color: #ab47bc;">BANQUET</td><td>+100</td><td>Confetti burst</td></tr>
      <tr><td style="color: #ffd700;">GOLDEN BUFFET</td><td>+250</td><td>Light rays</td></tr>
      <tr><td style="color: #00e5ff;">MYTHICAL FEAST</td><td>+500</td><td>Rainbow crown</td></tr>
    `;
    panel.appendChild(feastTable);

    return panel;
  }

  /**
   * Create the Powerups panel
   */
  private createPowerupsPanel(): HTMLDivElement {
    const panel = document.createElement('div');

    const table = document.createElement('table');
    table.className = 'help-menu-table';
    table.innerHTML = `
      <tr>
        <th>Icon</th>
        <th>Powerup</th>
        <th>Effect</th>
        <th>Bonus</th>
      </tr>
      <tr>
        <td style="font-size: 20px;">🧲</td>
        <td style="color: #9c27b0; font-weight: 600;">MAGNET</td>
        <td>Pulls nearby food towards you</td>
        <td>+5 segments</td>
      </tr>
      <tr>
        <td style="font-size: 20px;">⚡</td>
        <td style="color: #ff9800; font-weight: 600;">SPEED</td>
        <td>+12.5% speed per stack (max 4)</td>
        <td>+5 segments</td>
      </tr>
      <tr>
        <td style="font-size: 20px;">2x</td>
        <td style="color: #00bcd4; font-weight: 600;">2x SCORE</td>
        <td>Double points from food</td>
        <td>+5 segments</td>
      </tr>
      <tr>
        <td style="font-size: 20px;">5x</td>
        <td style="color: #00bcd4; font-weight: 600;">5x SCORE</td>
        <td>5x points from food</td>
        <td>+5 segments</td>
      </tr>
      <tr>
        <td style="font-size: 20px;">10x</td>
        <td style="color: #00e5ff; font-weight: 600;">10x SCORE</td>
        <td>10x points from food (Rare!)</td>
        <td>+5 segments</td>
      </tr>
      <tr>
        <td style="font-size: 20px;">🔄</td>
        <td style="color: #4caf50; font-weight: 600;">AGILITY</td>
        <td>Increased turn rate</td>
        <td>+5 segments</td>
      </tr>
    `;
    panel.appendChild(table);

    // Duration info
    const durationInfo = document.createElement('div');
    durationInfo.className = 'help-menu-tip';
    durationInfo.innerHTML = `
      <strong style="color: #4ecdc4;">Duration System:</strong><br>
      All powerups last <strong>30 seconds</strong> per pickup.<br>
      Collect more to stack duration up to <strong>10 minutes</strong> maximum!
    `;
    panel.appendChild(durationInfo);

    return panel;
  }

  /**
   * Create the Controls panel
   */
  private createControlsPanel(): HTMLDivElement {
    const panel = document.createElement('div');

    const table = document.createElement('table');
    table.className = 'help-menu-table';
    table.innerHTML = `
      <tr>
        <th>Input</th>
        <th>Action</th>
      </tr>
      <tr>
        <td><span class="help-menu-key">Mouse</span></td>
        <td>Aim direction - your worm follows the cursor</td>
      </tr>
      <tr>
        <td><span class="help-menu-key">Left Click</span></td>
        <td>Sprint - move faster (costs segments)</td>
      </tr>
      <tr>
        <td><span class="help-menu-key">Space</span></td>
        <td>Sprint (alternative)</td>
      </tr>
      <tr>
        <td><span class="help-menu-key">Shift</span></td>
        <td>Crawl mode - move slowly and safely</td>
      </tr>
      <tr>
        <td><span class="help-menu-key">Enter</span></td>
        <td>Open chat</td>
      </tr>
      <tr>
        <td><span class="help-menu-key">Tab</span></td>
        <td>Show scoreboard</td>
      </tr>
      <tr>
        <td><span class="help-menu-key">E</span></td>
        <td>Open emote wheel</td>
      </tr>
      <tr>
        <td><span class="help-menu-key">Scroll</span></td>
        <td>Zoom in/out</td>
      </tr>
      <tr>
        <td><span class="help-menu-key">Escape</span></td>
        <td>Pause menu</td>
      </tr>
    `;
    panel.appendChild(table);

    return panel;
  }

  /**
   * Create the AI Lore panel
   */
  private createAILorePanel(): HTMLDivElement {
    const panel = document.createElement('div');

    panel.innerHTML = `
      <div class="help-menu-section-title">
        <span>🐍</span> THE ARENA'S INHABITANTS
      </div>
    `;

    // Collectors
    const collectorsCard = document.createElement('div');
    collectorsCard.className = 'help-menu-lore-card';
    collectorsCard.style.borderColor = 'rgba(76, 175, 80, 0.4)';
    collectorsCard.innerHTML = `
      <div class="help-menu-lore-header">
        <span style="font-size: 24px;">🟢</span>
        <span class="help-menu-lore-tier" style="color: #4caf50;">COLLECTORS</span>
        <span style="color: #888; font-size: 12px;">(Harmless)</span>
      </div>
      <div class="help-menu-lore-names">
        Nibbles, Wiggles, Scales, Gulp, Crawler, Swallow, Glider, Earthworm
      </div>
      <div class="help-menu-lore-desc">
        "Peaceful food gatherers. Easy targets... or are they bait?"
      </div>
    `;
    panel.appendChild(collectorsCard);

    // Hunters
    const huntersCard = document.createElement('div');
    huntersCard.className = 'help-menu-lore-card';
    huntersCard.style.borderColor = 'rgba(255, 193, 7, 0.4)';
    huntersCard.innerHTML = `
      <div class="help-menu-lore-header">
        <span style="font-size: 24px;">🟡</span>
        <span class="help-menu-lore-tier" style="color: #ffc107;">HUNTERS</span>
        <span style="color: #888; font-size: 12px;">(Moderate)</span>
      </div>
      <div class="help-menu-lore-names">
        Chompy, Noodle, Zippy, Twister, Squirm, Slinky, Speedy, Munch, Rattler, Coil, Sidewinder, Fangface
      </div>
      <div class="help-menu-lore-desc">
        "Balanced predators. Will chase if they're bigger."
      </div>
    `;
    panel.appendChild(huntersCard);

    // Apex
    const apexCard = document.createElement('div');
    apexCard.className = 'help-menu-lore-card';
    apexCard.style.borderColor = 'rgba(244, 67, 54, 0.4)';
    apexCard.innerHTML = `
      <div class="help-menu-lore-header">
        <span style="font-size: 24px;">🔴</span>
        <span class="help-menu-lore-tier" style="color: #f44336;">APEX</span>
        <span style="color: #888; font-size: 12px;">(Dangerous)</span>
      </div>
      <div class="help-menu-lore-names">
        Slither, Viper, Crusher, Shadow, Venom, Hunter, Constrictor, Anaconda, Python, Fang
      </div>
      <div class="help-menu-lore-desc">
        "Aggressive hunters. They WILL chase you."
      </div>
    `;
    panel.appendChild(apexCard);

    // Leviathan
    const leviathanCard = document.createElement('div');
    leviathanCard.className = 'help-menu-lore-card';
    leviathanCard.style.borderColor = 'rgba(255, 215, 0, 0.6)';
    leviathanCard.style.background = 'linear-gradient(145deg, rgba(50, 40, 20, 0.8), rgba(30, 25, 15, 0.9))';
    leviathanCard.innerHTML = `
      <div class="help-menu-lore-header">
        <span style="font-size: 24px;">👑</span>
        <span class="help-menu-lore-tier" style="color: #ffd700; text-shadow: 0 0 10px rgba(255, 215, 0, 0.5);">LEVIATHAN</span>
        <span style="color: #ffd700; font-size: 12px;">(Legendary)</span>
      </div>
      <div class="help-menu-lore-desc" style="color: #ddd; font-style: italic;">
        "The apex of apex predators. LEVIATHAN doesn't chase - it <strong style="color: #ffd700;">INTERCEPTS</strong>.<br>
        It doesn't rush - it <strong style="color: #ffd700;">WAITS</strong>. When you see the crown, <strong style="color: #ff4444;">RUN</strong>.<br>
        But running might be exactly what it wants..."
      </div>
      <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(255, 215, 0, 0.2);">
        <div style="font-size: 12px; color: #ffc107; margin-bottom: 4px;">⚠️ Only ONE LEVIATHAN can exist at a time.</div>
        <div style="font-size: 12px; color: #4caf50;">🏆 Slaying LEVIATHAN grants bonus essence and eternal glory.</div>
      </div>
    `;
    panel.appendChild(leviathanCard);

    return panel;
  }

  /**
   * Create the Tips panel
   */
  private createTipsPanel(): HTMLDivElement {
    const panel = document.createElement('div');

    panel.innerHTML = `
      <div class="help-menu-section-title">
        <span>💡</span> PRO TIPS
      </div>
    `;

    const tips = [
      { icon: '🥗', text: 'Healthy food spawns more at the <strong style="color: #ffd700;">RIM (edge)</strong> - high risk, high reward!' },
      { icon: '⚡', text: 'Score under 20? Your sprint is <strong style="color: #4caf50;">FREE</strong> - use it to escape!' },
      { icon: '🎂', text: `Don't rush to feasts - <strong style="color: #ff4444;">trap those who do</strong>!` },
      { icon: '🧱', text: 'Use the edge as a wall to <strong style="color: #4ecdc4;">trap prey</strong>.' },
      { icon: '🌀', text: 'When coiled, spiral inside and wait - <strong style="color: #ffc107;">patience wins</strong>.' },
      { icon: '💀', text: 'Follow big fights - collect the aftermath <strong style="color: #4caf50;">safely</strong>.' },
      { icon: '👑', text: 'LEVIATHAN stalks before striking - <strong style="color: #ff4444;">check your minimap!</strong>' },
      { icon: '🔄', text: 'Sprint costs segments - use it wisely or you\'ll shrink!' },
      { icon: '🧲', text: 'Magnet powerup is great for <strong style="color: #9c27b0;">passive farming</strong>.' },
      { icon: '🏃', text: 'Speed stacks are multiplicative - <strong style="color: #ff9800;">4 stacks = 50% faster!</strong>' },
    ];

    for (const tip of tips) {
      const tipDiv = document.createElement('div');
      tipDiv.className = 'help-menu-tip';
      tipDiv.innerHTML = `<span style="font-size: 16px; margin-right: 8px;">${tip.icon}</span>${tip.text}`;
      panel.appendChild(tipDiv);
    }

    return panel;
  }

  /**
   * Create the Map panel
   */
  private createMapPanel(): HTMLDivElement {
    const panel = document.createElement('div');

    panel.innerHTML = `
      <div class="help-menu-section-title">
        <span>🗺️</span> ZONE MAP
      </div>
    `;

    const mapContainer = document.createElement('div');
    mapContainer.className = 'help-menu-zone-map';
    mapContainer.textContent = `
           NORTH
             │
    NW-RIM   │   NE-RIM      ← Best food, most danger
        ╲    │    ╱
    NW-MID   │   NE-MID      ← Balanced gameplay
        ╲    │    ╱
    NW-CORE  │  NE-CORE      ← Safe farming
 W-RIM──────●──────E-RIM
    SW-CORE  │  SE-CORE
        ╱    │    ╲
    SW-MID   │   SE-MID
        ╱    │    ╲
    SW-RIM   │   SE-RIM
             │
           SOUTH
`;
    panel.appendChild(mapContainer);

    // Zone explanations
    const zoneInfo = document.createElement('div');
    zoneInfo.style.marginTop = '16px';
    zoneInfo.innerHTML = `
      <table class="help-menu-table">
        <tr>
          <th>Zone</th>
          <th>Distance</th>
          <th>Food Quality</th>
          <th>Danger</th>
        </tr>
        <tr>
          <td style="color: #4caf50; font-weight: 600;">CORE</td>
          <td>0-33%</td>
          <td>70% Junk, 25% Regular, 5% Healthy</td>
          <td style="color: #4caf50;">Low</td>
        </tr>
        <tr>
          <td style="color: #ffc107; font-weight: 600;">MID</td>
          <td>33-66%</td>
          <td>50% Junk, 35% Regular, 15% Healthy</td>
          <td style="color: #ffc107;">Medium</td>
        </tr>
        <tr>
          <td style="color: #f44336; font-weight: 600;">RIM</td>
          <td>66-100%</td>
          <td>30% Junk, 40% Regular, 30% Healthy</td>
          <td style="color: #f44336;">High</td>
        </tr>
      </table>
    `;
    panel.appendChild(zoneInfo);

    return panel;
  }

  /**
   * Switch to a different tab
   */
  private switchTab(tab: HelpTab): void {
    if (tab === this.activeTab) return;

    // Update button styles
    for (const [id, btn] of this.tabButtons) {
      if (id === tab) {
        btn.style.background = 'linear-gradient(145deg, #4ecdc4, #3db3ab)';
        btn.style.color = '#fff';
        btn.style.boxShadow = '0 3px 12px rgba(78, 205, 196, 0.3)';
      } else {
        btn.style.background = 'rgba(40, 40, 60, 0.6)';
        btn.style.color = '#888';
        btn.style.boxShadow = 'none';
      }
    }

    // Update panel visibility
    for (const [id, panel] of this.tabPanels) {
      panel.style.display = id === tab ? 'block' : 'none';
    }

    this.activeTab = tab;
  }

  /**
   * Show the help menu
   */
  show(onClose?: () => void): void {
    if (this.visible) return;

    this.visible = true;
    this.onClose = onClose || null;
    this.container.style.display = 'flex';

    requestAnimationFrame(() => {
      this.container.style.opacity = '1';
    });

    // Add keyboard listener
    this.keydownListener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.hide();
      }
    };
    document.addEventListener('keydown', this.keydownListener, true);
  }

  /**
   * Hide the help menu
   */
  hide(): void {
    if (!this.visible) return;

    this.visible = false;

    // Remove keyboard listener
    if (this.keydownListener) {
      document.removeEventListener('keydown', this.keydownListener, true);
      this.keydownListener = null;
    }

    // Animate out
    this.container.style.opacity = '0';

    setTimeout(() => {
      this.container.style.display = 'none';
      if (this.onClose) {
        this.onClose();
        this.onClose = null;
      }
    }, 200);
  }

  /**
   * Toggle visibility
   */
  toggle(): void {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Check if visible
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Clean up
   */
  destroy(): void {
    if (this.keydownListener) {
      document.removeEventListener('keydown', this.keydownListener, true);
    }
    this.tabButtons.clear();
    this.tabPanels.clear();
    this.container.remove();
  }
}

/** Singleton instance */
let helpMenuInstance: HelpMenu | null = null;

/**
 * Get or create the singleton HelpMenu instance
 */
export function getHelpMenu(): HelpMenu {
  if (!helpMenuInstance) {
    helpMenuInstance = new HelpMenu();
  }
  return helpMenuInstance;
}
